import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Pencil, Trash2, Plus, FlaskConical, Package } from "lucide-react";
import { slidesList } from "@/lib/adminApi";

// Detailpagina van één inhoudsprofiel: profielgegevens, gekoppelde werkzame stof,
// gekoppelde producten en alle profielinhoud gerangschikt op volgorde.
const ProfileView = ({ profile, refs, onBack, openDialog, onDelete, reloadToken }) => {
  const [content, setContent] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const substance = (refs["werkzame-stoffen"] || []).find((s) => s.id === profile.werkzame_stof_id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        slidesList("profielinhoud", { inhoudsprofiel_id: profile.id, per_page: 200 }),
        slidesList("producten", { inhoudsprofiel_id: profile.id, per_page: 200 }),
      ]);
      setContent((c.rows || []).slice().sort((a, b) => (a.volgorde ?? 999) - (b.volgorde ?? 999)));
      setProducts(p.rows || []);
    } catch {
      toast.error("Kon profielgegevens niet laden");
    }
    setLoading(false);
  }, [profile.id, reloadToken]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="profile-view">
      <div className="flex items-center justify-between">
        <button onClick={onBack} data-testid="profile-back"
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
          <ArrowLeft className="h-4 w-4" /> Terug naar overzicht
        </button>
        <button onClick={() => openDialog("profielinhoud", null, { inhoudsprofiel_id: profile.id })} data-testid="profile-content-add"
          className="flex items-center gap-1.5 rounded-lg bg-[#0a5a3c] px-3 py-2 text-sm font-semibold text-white hover:bg-[#084a31]">
          <Plus className="h-4 w-4" /> Nieuwe inhoud
        </button>
      </div>

      {/* profielgegevens */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="profile-summary">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-[#0a2540]">{profile.naam}</h3>
            <p className="mt-1 text-sm text-slate-500">
              {[profile.werkzame_stof, profile.sterkte, profile.vorm].filter(Boolean).join(" · ") || "Geen extra gegevens"}
            </p>
          </div>
          <button onClick={() => openDialog("inhoudsprofielen", profile)} data-testid="profile-edit"
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#0a2540] hover:bg-slate-50">
            <Pencil className="h-3.5 w-3.5" /> Bewerk profiel
          </button>
        </div>
        {products.length > 0 && (
          <div className="mt-4">
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">Gekoppelde producten</p>
            <div className="flex flex-wrap gap-2" data-testid="profile-products">
              {products.map((p) => (
                <span key={p.id} className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                  <Package className="h-3 w-3" /> {p.naam} <span className="text-slate-400">CNK {(p.cnk || "").trim()}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* gekoppelde werkzame stof */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="profile-substance">
        <div className="flex items-start justify-between gap-4">
          <h4 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#0a5a3c]">
            <FlaskConical className="h-4 w-4" /> Werkzame stof{substance ? `: ${substance.naam}` : ""}
          </h4>
          {substance && (
            <button onClick={() => openDialog("werkzame-stoffen", substance)} data-testid="substance-edit"
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#0a2540] hover:bg-slate-50">
              <Pencil className="h-3.5 w-3.5" /> Bewerk stof
            </button>
          )}
        </div>
        {substance ? (
          <dl className="mt-3 space-y-3 text-sm">
            {[["Werking", substance.werking],
              ["Bijwerkingen — vaak", substance.bijwerkingen_vaak],
              ["Bijwerkingen — ernstig", substance.bijwerkingen_ernstig],
              ["Algemene waarschuwingen", substance.algemene_waarschuwingen]]
              .filter(([, v]) => v)
              .map(([label, v]) => (
                <div key={label}>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
                  <dd className="mt-0.5 whitespace-pre-line leading-relaxed text-slate-700">{v}</dd>
                </div>
              ))}
          </dl>
        ) : (
          <p className="mt-2 text-sm text-slate-400">Geen werkzame stof gekoppeld.</p>
        )}

        {/* vormspecifieke teksten (als het profiel een toedieningsvorm heeft) */}
        {profile.toedieningsvorm && substance && (() => {
          const norm = (t) => String(t || "").trim().toLowerCase().replace(/\s+/g, " ").replace(/[țţ]/g, "t");
          const extract = (n) => { const m = /\(([^)]+)\)\s*$/.exec(String(n || "").trim()); return norm(m ? m[1] : n); };
          const entry = (Array.isArray(substance.toedieningsvormen) ? substance.toedieningsvormen : [])
            .find((e) => extract(e.naam) === norm(profile.toedieningsvorm));
          return (
            <div className="mt-4 rounded-lg border border-slate-200 p-4" data-testid="profile-vorm-block">
              {entry ? (
                <>
                  <span className="rounded-full bg-[#0a5a3c]/10 px-2.5 py-1 text-xs font-semibold text-[#0a5a3c]">
                    vormspecifiek: {profile.toedieningsvorm}
                  </span>
                  <p className="mt-1.5 text-xs text-slate-400">De slideshow gebruikt voor dit profiel deze vormteksten:</p>
                  <dl className="mt-3 space-y-3 text-sm">
                    {[["Werking", entry.werking],
                      ["Bijwerkingen — vaak", entry.bijwerkingen_vaak],
                      ["Bijwerkingen — ernstig", entry.bijwerkingen_ernstig],
                      ["Algemene waarschuwingen", entry.algemene_waarschuwingen]]
                      .filter(([, v]) => v)
                      .map(([label, v]) => (
                        <div key={label}>
                          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
                          <dd className="mt-0.5 whitespace-pre-line leading-relaxed text-slate-700">{v}</dd>
                        </div>
                      ))}
                  </dl>
                </>
              ) : (
                <p className="text-sm text-amber-700">
                  Toedieningsvorm "{profile.toedieningsvorm}" ingesteld, maar geen overeenkomende vormtekst
                  gevonden bij deze stof — de algemene teksten hierboven worden gebruikt.
                </p>
              )}
            </div>
          );
        })()}
      </div>

      {/* profielinhoud op volgorde */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="profile-content">
        <div className="border-b border-slate-100 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Inhoud van dit profiel ({content.length === 1 ? "1 onderdeel" : `${content.length} onderdelen`})
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
        ) : content.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-slate-400">Nog geen inhoud — voeg het eerste onderdeel toe.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {content.map((row) => (
              <li key={row.id} className="flex items-start gap-3 px-4 py-3" data-testid={`profile-content-row-${row.id}`}>
                <span className="mt-0.5 grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-[#0a2540]/5 text-xs font-bold text-[#0a2540]">
                  {row.volgorde ?? "–"}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="rounded-full bg-[#0a5a3c]/10 px-2 py-0.5 text-xs font-semibold text-[#0a5a3c]">{row.titel}</span>
                  <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-slate-700">{row.inhoud || "—"}</p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <button onClick={() => openDialog("profielinhoud", row)} data-testid={`content-edit-${row.id}`}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#0a2540] hover:bg-slate-50">
                    <Pencil className="h-3.5 w-3.5" /> Bewerk
                  </button>
                  <button onClick={() => onDelete("profielinhoud", row, `onderdeel "${row.titel}"`)} data-testid={`content-delete-${row.id}`}
                    className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ProfileView;
